import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = '';

// Default content fallback
const defaultContent = {
  hero: {
    badge: 'Développeur & Aménageur Territorial',
    title: ['Façonner le', 'territoire', 'de demain'],
    subtitle: 'One Green Dev conçoit et développe des projets immobiliers d\'exception en Côte d\'Ivoire, alliant innovation, durabilité et valorisation du territoire.',
    stats: [
      { value: 3, suffix: '', label: 'Projets', sublabel: 'majeurs' },
      { value: 500, suffix: '+', label: 'Hectares', sublabel: 'aménagés' },
      { value: 15, suffix: '+', label: 'Années', sublabel: 'd\'expertise' }
    ],
    cta_primary: 'Découvrir nos projets',
    cta_secondary: 'Nous contacter',
    slides: [
      { image: 'https://images.pexels.com/photos/30804375/pexels-photo-30804375.jpeg', label: 'Développement territorial' },
      { image: 'https://images.pexels.com/photos/914682/pexels-photo-914682.jpeg', label: 'Le Golf de Songon' },
      { image: 'https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg', label: 'Les Terres de Songon' }
    ]
  },
  trackRecord: {
    label: 'Track Record',
    title: 'Notre engagement en chiffres',
    stats: [
      { value: 500, suffix: '+', label: 'Hectares', sublabel: 'de foncier maîtrisé' },
      { value: 3, suffix: '', label: 'Projets', sublabel: 'en développement' },
      { value: 15, suffix: '+', label: 'Années', sublabel: 'd\'expertise' },
      { value: 100, suffix: '%', label: 'Engagement', sublabel: 'qualité & durabilité' }
    ]
  },
  about: {
    label: 'À propos de nous',
    title: ['Développeur territorial', 'de référence'],
    intro: 'One Green Dev est un acteur majeur du développement territorial en Côte d\'Ivoire, spécialisé dans la conception et la réalisation de projets immobiliers d\'envergure qui allient innovation, qualité et respect de l\'environnement.',
    mission: {
      title: 'Notre Mission',
      text: 'Créer des espaces de vie d\'exception qui contribuent au développement économique et social de la Côte d\'Ivoire, tout en préservant et valorisant son patrimoine naturel.',
      points: ['Développement local durable', 'Création d\'emplois', 'Excellence architecturale']
    },
    vision: {
      title: 'Notre Vision',
      text: 'Devenir le développeur territorial de référence en Afrique de l\'Ouest, reconnu pour notre capacité à transformer des territoires en créant de la valeur partagée pour toutes les parties prenantes.',
      points: ['Leader régional', 'Innovation continue', 'Impact positif durable']
    },
    expertise: [
      { title: 'Développement Immobilier', desc: 'Conception et réalisation de projets résidentiels et commerciaux' },
      { title: 'Aménagement Durable', desc: 'Intégration environnementale et développement responsable' },
      { title: 'Excellence Opérationnelle', desc: 'Standards internationaux et qualité irréprochable' }
    ],
    team: {
      title: 'Notre Équipe',
      text: 'Une équipe pluridisciplinaire d\'experts passionnés, réunissant des compétences en développement immobilier, ingénierie, finance et gestion de projets.',
      stats: [
        { value: '20+', label: 'Collaborateurs' },
        { value: '5', label: 'Départements' },
        { value: '15+', label: 'Ans d\'expérience' }
      ],
      image: 'https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg'
    }
  },
  partners: {
    label: 'Nos Partenaires',
    title: 'Ils nous font confiance',
    subtitle: 'Des partenaires de premier plan nous accompagnent dans la réalisation de nos projets d\'envergure.',
    categories: [
      { name: 'Partenaire Finance', category: 'Finance' },
      { name: 'Partenaire Construction', category: 'Construction' },
      { name: 'Partenaire Architecture', category: 'Architecture' },
      { name: 'Partenaire Ingénierie', category: 'Ingénierie' },
      { name: 'Partenaire Juridique', category: 'Juridique' },
      { name: 'Partenaire Environnement', category: 'Environnement' }
    ],
    cta: {
      title: 'Devenir partenaire ?',
      text: 'Rejoignez notre réseau de partenaires et participez au développement de projets d\'envergure en Côte d\'Ivoire.',
      button: 'Nous contacter'
    }
  },
  contact: {
    phone: '+225 07 05 50 97 38',
    email: 'contact@onegreendev.com',
    address: 'Abidjan, Côte d\'Ivoire',
    hours: {
      weekdays: '8h00 - 18h00',
      saturday: '9h00 - 13h00',
      sunday: 'Fermé'
    }
  }
};

const CMSContext = createContext(null);

export const CMSProvider = ({ children }) => {
  const [content, setContent] = useState(defaultContent);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/content/onegreendev`);
        if (response.ok) {
          const data = await response.json();
          // Merge fetched content with defaults (fetched takes priority)
          if (data && !data.use_default) {
            setContent(prev => ({ ...prev, ...data.content }));
          }
        }
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, []);

  const value = {
    content,
    isLoading,
    error,
    // Helper to get nested content with fallback
    get: (path, fallback = '') => {
      const keys = path.split('.');
      let result = content;
      for (const key of keys) {
        result = result?.[key];
        if (result === undefined) return fallback;
      }
      return result;
    }
  };

  return (
    <CMSContext.Provider value={value}>
      {children}
    </CMSContext.Provider>
  );
};

export const useCMS = () => {
  const context = useContext(CMSContext);
  if (!context) {
    throw new Error('useCMS must be used within a CMSProvider');
  }
  return context;
};

export default CMSContext;
